import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ViewIcon } from '../../../../assets/icons/background-eye-icon.svg';
import { ReactComponent as ActionEditIcon } from '../../../../assets/icons/border-edit-pencil.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { AccountListInterface } from '../../../../interface/account-list-penalty-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchAccountListFilter,
  fetchAccountsList,
  setSortData,
  updateAccountListFilter,
  updatedSingleAccount,
} from '../../../../redux/slices/PenaltyAccountListSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../../shared-components/switch-button-field/SwitchButton';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import UTCtDateoDateConvert from '../../../../shared-functions/UTCtDateoDateConvert';
import penaltyManagementApi from '../../../../utils/api/penalty-management';
import Override from '../Override';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal?: () => void;
  rightCorner: string;
}

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}
/**
 * Account Pages
 * @returns {any} The rendered component
 */
function AccountListPenalty() {
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { t, i18n } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const {
    accountList,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.accountPenalty);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    rightCorner: 'ok',
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: 'ok',
    notification: false,
  });

  /**
   * Confirm the status change function
   * @param {string} id The account ID
   * @param {boolean} status The new status
   */
  const confirmStatusChange = (id: string, status: boolean) => {
    penaltyManagementApi
      .updateSingleAccountStatus(id, status)
      .then((response) => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          content: (
            <SuccessNotification
              title={t('statusChangedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                dispatch(updatedSingleAccount(response.data));
              }}
            />
          ),
          rightCorner: '',
          notificationModal: true,
          header: false,
          /**
           * Close modal with function to dispatch accountList details
           */
          closeModal: () => {
            setModalBoxStatus('close');
            dispatch(updatedSingleAccount(response.data));
          },
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {any} account - The account to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (account: AccountListInterface, checked: boolean) => {
    if (checked) {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: t('disableDeviceWarning'),
        content: (
          <DisableSwitchWarning
            selectedId={account._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('enablePenaltyConfirm')}
            onConfirm={() => confirmStatusChange(account._id, checked)}
          />
        ),
        notificationModal: true,
        header: false,
        rightCorner: '',
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
    } else {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: t('disableDeviceWarning'),
        content: (
          <DisableSwitchWarning
            selectedId={account._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('disablePenaltyConfirm')}
            onConfirm={() => confirmStatusChange(account._id, checked)}
          />
        ),
        notificationModal: true,
        header: false,
        rightCorner: '',
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
    }
  };

  /**
   * Renders the custom data for the availability column
   * @param {any} account - The device data
   * @returns {any} The rendered availability toggle cell
   */
  const renderAvailability = (account: AccountListInterface) => (
    <div className="flex justify-center">
      <div key={account.id}>
        <SwitchButton
          checked={account.status}
          change={(checked: boolean) => handleAvailabilityChange(account, checked)}
        />
      </div>
    </div>
  );

  /**
   * Modal box close function
   */
  const closeModalFunction = () => {
    setOpenStatus(false);
  };

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: any) => {
    if (!loading) {
      dispatch(fetchAccountsList({ ...data }));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page,
      limit,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Edit account function
   * @param {any} currentData - The currentData
   */
  const editAccountFunction = (currentData: AccountListInterface) => {
    if (currentData && Object.keys(currentData).length > 0) {
      setOpenStatus(true);
      setModalView({
        title: t('editOverride'),
        header: false,
        content: (
          <Override
            title={`${t('account')} ${t('editOverride')}`}
            id={currentData._id}
            type="edit"
            list="account"
            closeModal={closeModalFunction}
            handleRefresh={handleRefresh}
          />
        ),
        rightCorner: '',
        notification: false,
      });
    }
  };

  /**
   * View account
   * @param {any} currentData The account details
   */
  const ViewAccount = (currentData: AccountListInterface) => {
    if (currentData && Object.keys(currentData).length > 0) {
      setOpenStatus(true);
      setModalView({
        title: t('editOverride'),
        header: false,
        content: (
          <Override
            title={`${t('account')} ${t('viewOverride')}`}
            id={currentData._id}
            type="view"
            list="account"
            closeModal={closeModalFunction}
            handleRefresh={handleRefresh}
          />
        ),
        rightCorner: '',
        notification: false,
      });
    }
  };

  /**
   * AddOverrides
   */
  const handleAddOverride = () => {
    setOpenStatus(true);
    setModalView({
      title: t('addOverride'),
      header: false,
      content: (
        <Override
          title={t('addOverride')}
          closeModal={closeModalFunction}
          type="add"
          list="account"
          handleRefresh={handleRefresh}
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Renders the custom data for the actions column
   * @param {any} currentData - The Account data
   * @returns {any} The rendered actions cell
   */
  const renderActions = (currentData: AccountListInterface) => (
    <div className="action-buttons" role="presentation">
      <ViewIcon
        color="var(--primary-color)"
        fill="var(--primary-color)"
        onClick={() => ViewAccount(currentData)}
      />
      <ActionEditIcon
        color="var(--primary-color)"
        fill="var(--primary-color)"
        onClick={() => {
          editAccountFunction(currentData);
        }}
      />
    </div>
  );

  /**
   * Renders custom data for the Dynamic Pricing column
   * @param {any} currentRow - The current row data
   * @returns {any} The rendered cell
   */
  const renderDynamicPricing = (currentRow: AccountListInterface) => {
    return (
      <div className="dynamic-pricing-tags">
        {currentRow.dynamicPricing.map((item) => (
          <span key={item} className="pricing-chip">
            {item
              .replace(/([A-Z])/g, ' $1') // offPeak → off Peak
              .replace(/^./, (char) => char.toUpperCase())}
          </span>
        ))}
      </div>
    );
  };

  /**
   * Renders custom data for the Grace Interval column
   * @param {any} currentRow - The current row data
   * @returns {any} The rendered grace interval cell
   */
  const renderGraceInterval = (currentRow: AccountListInterface) => {
    return currentRow.graceInterval ? (
      <p>
        {currentRow.graceInterval} {t('min')}
      </p>
    ) : (
      <div />
    );
  };

  /**
   * Renders custom data for the Ceiling Type column
   * @param {any} currentRow - The current row data
   * @returns {any} The rendered ceiling type cell
   */
  const renderCeilingType = (currentRow: AccountListInterface) => {
    const formattedValue = currentRow.ceilingType
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (char) => char.toUpperCase());
    return <p>{formattedValue}</p>;
  };

  /**
   * Renders custom data for the Effective Date column
   * @param {any} currentRow - The current row data
   * @returns {any} The rendered effective date cell
   */
  const renderEffectiveDate = (currentRow: AccountListInterface) => {
    return (
      <p>{`${UTCtDateoDateConvert(currentRow.effectiveFrom)} - ${UTCtDateoDateConvert(currentRow.effectiveTo)}`}</p>
    );
  };

  const column = [
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof AccountListInterface,
      sortAllow: false,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof AccountListInterface,
      sortAllow: true,
    },
    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof AccountListInterface,
      sortAllow: true,
      customData: renderDynamicPricing,
      columnWidth: 200,
    },
    {
      label: t('graceInterval'),
      accessorKey: 'graceInterval' as keyof AccountListInterface,
      sortAllow: true,
      customData: renderGraceInterval,
    },
    {
      label: t('ceilingType'),
      accessorKey: 'ceilingType' as keyof AccountListInterface,
      sortAllow: false,
      customData: renderCeilingType,
    },
    {
      label: t('maxCeilingValue'),
      accessorKey: 'ceilingValue' as keyof AccountListInterface,
      sortAllow: true,
      /**
       * maxCeilingValue custom function
       * @param {any} currentRow - The currentRow
       */ customData: (currentRow: AccountListInterface) => (
        <div>{ToFixedValue(currentRow.ceilingValue)}</div>
      ),
    },
    {
      label: t('effectiveDate'),
      accessorKey: 'effectiveFrom' as keyof AccountListInterface,
      sortAllow: true,
      customData: renderEffectiveDate,
    },
    {
      label: t('enableDisable'),
      accessorKey: 'status' as keyof AccountListInterface,
      customData: renderAvailability,
    },
    {
      label: t('action'),
      accessorKey: '' as keyof AccountListInterface,
      sortAllow: false,
      customData: renderActions,
      columnWidth: 70,
    },
  ];

  /**
   * Table pagination function
   * @param {any} value the paginate value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Current columns for Account Override export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('accountName'), accessorKey: 'accountName' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },
    { label: t('graceInterval'), accessorKey: 'graceInterval' },
    { label: t('ceilingType'), accessorKey: 'ceilingType' },
    { label: t('maxCeilingValue'), accessorKey: 'ceilingValue' },
    { label: t('effectiveDate'), accessorKey: 'effectiveDate' },
    { label: t('status'), accessorKey: 'status' },
  ];

  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    setExportLoader(true);
    penaltyManagementApi
      .accountOverridesExport(payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AccountListInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'accountCustomId':
                return row?.accountCustomId || t('na');

              case 'accountName':
                return row?.accountName || t('na');

              case 'dynamicPricing':
                return row?.dynamicPricing?.length
                  ? row.dynamicPricing
                      .map((item) =>
                        item.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase()),
                      )
                      .join(', ')
                  : t('na');

              case 'graceInterval':
                return row?.graceInterval ? `${row.graceInterval} ${t('min')}` : t('na');

              case 'ceilingType':
                return row?.ceilingType
                  ? row.ceilingType
                      .replace(/([a-z])([A-Z])/g, '$1 $2')
                      .replace(/^./, (char) => char.toUpperCase())
                  : t('na');

              case 'ceilingValue':
                return row?.ceilingValue ? ToFixedValue(row.ceilingValue) : '0.00';

              case 'effectiveDate':
                return `${UTCtDateoDateConvert(
                  row.effectiveFrom,
                )} - ${UTCtDateoDateConvert(row.effectiveTo)}`;

              case 'status':
                return row?.status ? t('enabled') : t('disabled');

              default:
                return t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'account-overrides-list.csv');
          } else {
            exportPDF(headers, rows, 'account-overrides-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      limit,
      page: DefaultApiLimit.page,
    });
  };

  useEffect(() => {
    if (isFilterUpdated || accountList.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
    }
  }, [filters]);

  return (
    <div className="penalty-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal ?? (() => setModalBoxStatus('close'))}
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
        rightCorner=""
      />
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
        position="bottom-right"
      />
      <CustomTable
        version="v2"
        header={column}
        data={accountList}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        pageLimitChange={pageNateLimitChange}
        handleSortChange={handleSortFunction}
        filters={filters}
        actionButton
        height={window.innerHeight >= 700 ? 347 : 349}
        filtersToDispatch={updateAccountListFilter}
        filterListLoading={filterListLoading}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        filterListToDispatch={fetchAccountListFilter}
        exportLoader={exportLoader}
        exportData={accountList}
        refershCurrentPages={handleRefresh}
        refreshRequired
        hoverLabel={t('refresh')}
        sizeChangeButtonRequired
        leftTopContent={<div> </div>}
        rightTopContent={
          <div>
            <MyButton
              label={t('addOverride')}
              buttonType="submit"
              onClickFunc={handleAddOverride}
            />
          </div>
        }
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default AccountListPenalty;
